/**
 * shadcn-style DropdownMenu wrapper around Radix DropdownMenu.
 *
 * Wave-5 fix 5.x.4 / UI-review C-3: replaces a hand-rolled `<div>` popover
 * in RootLayout. The Radix primitive provides — for free — every a11y
 * affordance the custom popover was missing:
 *
 *   - Esc-to-close
 *   - Tab and Arrow-key keyboard navigation between menu items
 *   - Return-focus to the trigger when the menu closes
 *   - WAI-ARIA `menu` / `menuitem` roles + `aria-expanded` on the trigger
 *   - Click-outside-to-close (without our hand-rolled mousedown listener)
 *   - Type-ahead navigation
 *
 * The wrapper is intentionally thin: only the visual styling we need
 * (rounded surface, border, shadow, item hover) is added; everything
 * else passes through verbatim. Same shape as our existing dialog.tsx so
 * future consumers don't have to learn a second pattern.
 */
import * as React from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';

import { cn } from '@/lib/utils';

const DropdownMenu = DropdownMenuPrimitive.Root;
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
const DropdownMenuPortal = DropdownMenuPrimitive.Portal;
const DropdownMenuGroup = DropdownMenuPrimitive.Group;
const DropdownMenuLabel = DropdownMenuPrimitive.Label;
const DropdownMenuSeparator = DropdownMenuPrimitive.Separator;

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <DropdownMenuPortal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        // Surface / border / shadow tuned to match our existing popover
        // visual idiom (border-line + bg-surface + shadow-2). Width is
        // controlled by the consumer; we only set min-w to keep the
        // dropdown from collapsing when items are short.
        'z-50 min-w-[10rem] overflow-hidden rounded-md border border-line bg-surface p-1 text-ink shadow-2',
        // Animations match dialog.tsx so portalled content feels coherent.
        'data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out',
        className,
      )}
      {...props}
    />
  </DropdownMenuPortal>
));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      // Touch-target (impeccable audit P2 / WCAG 2.5.5): menu items are the
      // actual actions behind the row kebab. `py-1.5` yields a ~33px row —
      // under 44px — so floor min-height on COARSE pointers only. Items stack
      // vertically, so this grows the row cleanly; `pointer: fine` is unchanged.
      'relative flex cursor-default select-none items-center rounded-sm px-3 py-1.5 text-sm outline-none transition-colors [@media(pointer:coarse)]:min-h-[44px]',
      // Radix sets `data-highlighted` when the item has roving focus
      // (keyboard or pointer). Use that one selector for both states so
      // we don't duplicate the hover/focus colours.
      'data-[highlighted]:bg-surface-2 data-[highlighted]:text-ink',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    {...props}
  />
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
};
